import React, { useCallback, useEffect, useMemo, useState } from "react";
import { appUsageApi, usersApi, productsApi } from "../../services/api.service";
import { T, SectionLabel, Field, Input, Select, Btn } from "../shared/UI";
import { onlyOnAgents } from "../../utils/normalizers";

function formatUsage(seconds) {
  const s = Number(seconds) || 0;
  if (s <= 0) return "—";
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m <= 0) return `${rem}s`;
  if (rem === 0) return `${m}m`;
  return `${m}m ${rem}s`;
}

export default function AppUsageView() {
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({
    agentId: "",
    agentName: "",
    productId: "",
    productName: "",
    app: "",
    fromDate: today,
    toDate: today,
    search: "",
  });

  const agentOptions = useMemo(
    () => agents.map(a => ({ id: a.id, name: a.fullName || a.name || a.mobile })),
    [agents]
  );
  const productOptions = useMemo(
    () => products.map(p => ({ id: p.id, name: p.name })),
    [products]
  );

  const loadAgents = useCallback(async () => {
    try {
      const res = await usersApi.agentsMeta();
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setAgents(onlyOnAgents(raw));
    } catch {
      setAgents([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const res = await productsApi.list();
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setProducts(raw);
    } catch {
      setProducts([]);
    }
  }, []);

  const loadUsage = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: meta.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== "" && v != null)
        ),
      };
      const res = await appUsageApi.list(params);
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const metaRes = res.data?.meta || { page, limit: meta.limit, total: raw.length, totalPages: 1 };
      setRows(raw);
      setMeta({
        page: metaRes.page || page,
        limit: metaRes.limit || meta.limit,
        total: metaRes.total || raw.length,
        totalPages: metaRes.totalPages || 1,
      });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load app usage";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, meta.limit]);

  // Exports every row matching the current filters — the server streams the full
  // filtered dataset as a CSV file (not just the page shown on screen).
  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v != null)
      );
      const res = await appUsageApi.exportCsv(activeFilters);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `app-usage-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Export failed";
      setError(msg);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadUsage(1); }, [loadUsage]);

  return (
    <div style={styles.wrap}>
      <div style={styles.toolbar}>
        <SectionLabel>APP USAGE</SectionLabel>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Btn variant="ghost" onClick={() => loadUsage(1)}>Apply</Btn>
          <Btn variant="outline" onClick={exportCsv} disabled={!rows.length || exporting}>
            {exporting ? "Exporting…" : "Export CSV"}
          </Btn>
        </div>
      </div>

      <p style={styles.hint}>
        Other apps used by executives on their phones, reported every 15 minutes.
      </p>

      <div style={styles.filters}>
        <Field label="AGENT">
          <Select value={filters.agentId} onChange={e => setFilters(f => ({ ...f, agentId: e.target.value }))}>
            <option value="">All</option>
            {agentOptions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <Field label="AGENT NAME">
          <Input value={filters.agentName} onChange={e => setFilters(f => ({ ...f, agentName: e.target.value }))} />
        </Field>
        <Field label="PRODUCT">
          <Select value={filters.productId} onChange={e => setFilters(f => ({ ...f, productId: e.target.value }))}>
            <option value="">All</option>
            {productOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label="APP">
          <Input value={filters.app} onChange={e => setFilters(f => ({ ...f, app: e.target.value }))} placeholder="name / package" />
        </Field>
        <Field label="FROM DATE">
          <Input type="date" value={filters.fromDate} onChange={e => setFilters(f => ({ ...f, fromDate: e.target.value }))} />
        </Field>
        <Field label="TO DATE">
          <Input type="date" value={filters.toDate} onChange={e => setFilters(f => ({ ...f, toDate: e.target.value }))} />
        </Field>
        <Field label="SEARCH">
          <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="app/package" />
        </Field>
      </div>

      {loading && <div style={styles.info}>Loading app usage…</div>}
      {error && <div style={{ ...styles.info, color: T.sos }}>{error}</div>}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {["Agent","Product","App","Package","Usage","Reported At"].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={styles.tr}>
                <td style={styles.td}>
                  <div style={{ fontWeight: 600 }}>{r.agentName || r.agentId}</div>
                </td>
                <td style={styles.tdM}>{r.productName || "—"}</td>
                <td style={styles.td}>{r.appName || "—"}</td>
                <td style={styles.tdM}>{r.packageName}</td>
                <td style={styles.tdM}>{formatUsage(r.usageSeconds)}</td>
                <td style={styles.tdM}>{r.reportedAt}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td style={styles.empty} colSpan={6}>No app usage found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.pagination}>
        <Btn size="sm" variant="ghost" disabled={meta.page <= 1} onClick={() => loadUsage(meta.page - 1)}>Prev</Btn>
        <span style={styles.pageLabel}>Page {meta.page} of {meta.totalPages}</span>
        <Btn size="sm" variant="ghost" disabled={meta.page >= meta.totalPages} onClick={() => loadUsage(meta.page + 1)}>Next</Btn>
      </div>
    </div>
  );
}

const styles = {
  wrap: { flex:1, overflowY:"auto", padding:20, display:"flex", flexDirection:"column", gap:16 },
  toolbar: { display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" },
  hint: { margin:0, fontSize:12, color:T.muted, maxWidth:560, lineHeight:1.5 },
  filters: { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:12, background:T.s2, border:`1px solid ${T.border}`, borderRadius:12, padding:12 },
  info: { fontSize:12, color:T.muted, padding:"6px 4px" },
  tableWrap: { background:T.s2, border:`1px solid ${T.border}`, borderRadius:12, padding:12, overflowX:"auto" },
  table: { width:"100%", borderCollapse:"collapse" },
  th: { textAlign:"left", fontFamily:T.fontMono, fontSize:9, letterSpacing:"1.5px", color:T.muted, padding:"6px 10px", borderBottom:`1px solid ${T.border}`, textTransform:"uppercase", whiteSpace:"nowrap" },
  tr: { borderBottom:`1px solid ${T.border}` },
  td: { padding:"8px 10px", fontSize:12, color:T.text, verticalAlign:"middle", whiteSpace:"nowrap" },
  tdM: { padding:"8px 10px", fontSize:11, color:T.muted, verticalAlign:"middle", whiteSpace:"nowrap" },
  empty: { padding:"16px 10px", fontSize:12, color:T.muted, textAlign:"center" },
  pagination: { display:"flex", alignItems:"center", justifyContent:"space-between" },
  pageLabel: { fontSize:11, color:T.muted, fontFamily:T.fontMono },
};
